"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { useQuoteContext } from "@/contexts/quote-context";
import { useState } from "react";

interface CouponComponentProps {
  onApplyCoupon?: (coupon: string) => void;
}

export default function CouponComponent({
  onApplyCoupon,
}: CouponComponentProps) {
  const { cotacao, applyCoupon, cuponsDisponiveis } = useQuoteContext();
  const [coupon, setCoupon] = useState(cotacao?.cup_nome || "");
  const [isApplied, setIsApplied] = useState(!!cotacao?.cup_codigo);
  const [isError, setIsError] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleApplyCoupon = async () => {
    if (!coupon || !cotacao) return;
    const cupomJaAplicado =
      cotacao.cup_nome?.toLowerCase() === coupon.toLowerCase();

    const cupomEncontrado = cuponsDisponiveis.find(
      (c) => c.cup_nome.toLowerCase() === coupon.toLowerCase()
    );

    if (cupomJaAplicado) {
      toast({
        title: "Cupom já aplicado",
        description: `O cupom ${coupon} já está ativo na sua cotação.`,
      });
      return;
    }

    if (!cupomEncontrado) {
      toast({
        title: "Cupom inválido",
        description:
          "Verifique se digitou corretamente ou se o cupom está ativo.",
        variant: "alert",
      });
      setIsDisabled(true);
      return;
    }

    setIsLoading(true);

    const success = await applyCoupon(cupomEncontrado);

    setIsLoading(false);

    if (success) {
      setIsApplied(true);
      toast({
        title: "Cupom aplicado!",
        description: `Cupom ${cupomEncontrado.cup_nome} aplicado com sucesso.`,
        variant: "green",
      });
      if (onApplyCoupon) {
        onApplyCoupon(coupon);
      }
    } else {
      toast({
        title: "Erro ao aplicar cupom",
        description: "Tente novamente mais tarde ou verifique o cupom.",
        variant: "destructive",
      });
      setIsError(true);
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex gap-1">
        <Input
          id="coupon"
          placeholder="Cupom de desconto"
          value={coupon}
          onChange={(e) => {
            setCoupon(e.target.value);
            setIsApplied(false);
            setIsDisabled(false);
            setIsError(false);
          }}
          disabled={isLoading}
          className="flex-1 rounded-r-sm"
        />
        <Button
          type="button"
          variant={"default"}
          onClick={handleApplyCoupon}
          disabled={!coupon || isLoading}
          className="whitespace-nowrap rounded-l-sm rounded-r-xl text-sm md:text-base"
        >
          {isLoading ? "Aplicando..." : "Aplicar"}
        </Button>
      </div>
      {isApplied && (
        <p className="text-xs font-semibold text-n-green-light">
          Cupom {cotacao?.cup_nome} aplicado com sucesso.
        </p>
      )}

      {isError && (
        <p className="text-xs text-n-text-neutral">
          Erro ao aplicar cupom. Tente novamente mais tarde.
        </p>
      )}
      {isDisabled && (
        <p className="text-xs text-n-text-neutral">
          Cupom desabilitado ou expirado
        </p>
      )}
    </div>
  );
}
